/* eslint-disable check-react-ssr-usage/guard-browser-globals -- Client-only instrumentation; app keys are recorded as 3P apps render on the client. */

/**
 * Aggregation of every third-party (3P) app key observed during the current
 * navigation, from both collection sites:
 *
 * - **Content apps** — legacy/Connect macros rendered inside page content,
 *   recorded on `window.__APP_KEYS__` by `LegacyMacroRendererComponent`.
 * - **Non-content apps** — byline, page banner, background script, content
 *   tools, web panels and web items, recorded via `recordNonContentAppKeys`.
 *
 * Consumed by `pageLoadEventPlugin` to populate the `appKeys` (all navigations)
 * and `connectAppKeys` (initial page load only) page-load attributes.
 */

import { getNonContentAppKeys, resetNonContentAppKeys } from './NonContentAppKeys.ts';

declare global {
	interface Window {
		__APP_KEYS__?: string[];
	}
}

/**
 * All content and non-content 3P app keys recorded so far for the current
 * navigation, deduplicated and joined into a single comma-separated string so
 * it can be sent as one `extra_properties` value.
 */
export const getAppKeys = (): string => {
	if (typeof window === 'undefined') {
		return '';
	}

	return [...new Set([...(window.__APP_KEYS__ ?? []), ...getNonContentAppKeys()])].join(',');
};

/**
 * Clear all recorded 3P app keys. Intended to be called at the start of a route
 * transition so keys do not accumulate across navigations, and for test
 * cleanup.
 */
export const resetAppKeys = (): void => {
	if (typeof window === 'undefined') {
		return;
	}

	window.__APP_KEYS__ = [];
	resetNonContentAppKeys();
};
